import { FormikErrors, FormikTouched } from 'formik'
import {
  CreateLocationFromAgreementProps,
  CreateServiceLocationFormModel,
} from '../utility/types'
import { useCallback } from 'react'
import {
  ValidatedLocation,
  VerifiedLocationsForMatch,
} from '@user/components/Location/MatchCreateNew/typings'
import {
  ENTITY_TYPES,
  isValidPhone,
  isValidPhoneExtension,
  useAppDispatch,
} from '@sales-center/common'
import {
  addEditEntityLocation,
  LocationInput,
} from '@user/store/entityLocations'
import { toast } from '@sales-center/components'
import { useLocalisedValidations } from '@user/utils/useLocalisedValidations'
import { validateEmailCollection } from '@user/components/EmailCollection/validateEmailCollection'
import { locationUnder30PP } from '@user/components/Settings/EntityFormSettings'

export function useLocationHandlers(addToPestPac?: boolean) {
  const dispatch = useAppDispatch()
  const { validatePostalCode } = useLocalisedValidations()

  const validate = useCallback(
    (
      values: CreateLocationFromAgreementProps,
    ): FormikErrors<CreateLocationFromAgreementProps> => {
      const locations = Object.entries(values.locations).map(
        ([_, location]) => location,
      )

      let errors: FormikErrors<CreateLocationFromAgreementProps> = {}

      errors.locations = {}

      for (let i = 0; i < locations.length; i++) {
        const locationErrors: FormikErrors<VerifiedLocationsForMatch> = {}

        const {
          firstName,
          lastName,
          company,
          phoneNumber,
          altPhoneNumber,
          phoneExt,
          altPhoneExt,
          address1,
          city,
          state,
          postalCode,
          id,
          emailAddresses,
        } = locations[i]

        const hasFullName = firstName?.trim() !== '' || lastName?.trim() !== ''
        const hasCompany = company?.trim() !== ''
        const nameRequired = !hasCompany
        const companyRequired = !hasFullName

        const hasEmail = emailAddresses.find(
          (x) => x.emailAddress?.trim() !== '',
        )
        const hasPhone = phoneNumber?.trim() !== ''
        const emailRequired = !hasPhone && !hasEmail
        const phoneRequired = !hasEmail

        if (nameRequired && !hasFullName) {
          if (!firstName?.trim()) {
            locationErrors.firstName = 'Required'
            errors.locations[id] = locationErrors
          }

          if (!lastName?.trim()) {
            locationErrors.lastName = 'Required'
            errors.locations[id] = locationErrors
          }
        }

        if (companyRequired) {
          if (!company?.trim()) {
            locationErrors.company = 'Required'
            errors.locations[id] = locationErrors
          }
        }

        if (!address1?.trim()) {
          locationErrors.address1 = 'Required'
          errors.locations[id] = locationErrors
        }

        if (addToPestPac && address1?.trim() && address1?.trim().length > 30) {
          locationErrors.address1 = locationUnder30PP
          errors.locations[id] = locationErrors
        }

        if (!city?.trim()) {
          locationErrors.city = 'Required'
          errors.locations[id] = locationErrors
        }

        if (!state?.trim()) {
          locationErrors.state = 'Required'
          errors.locations[id] = locationErrors
        }

        if (!postalCode?.trim()) {
          locationErrors.postalCode = 'Required'
          errors.locations[id] = locationErrors
        }

        if (phoneRequired && !phoneNumber?.trim()) {
          locationErrors.phoneNumber = 'Required'
          errors.locations[id] = locationErrors
        } else if (hasPhone && !isValidPhone(phoneNumber)) {
          locationErrors.phoneNumber = 'Must be valid phone number'
          errors.locations[id] = locationErrors
        }

        if (phoneExt !== '' && !isValidPhoneExtension(phoneExt ?? '')) {
          locationErrors.phoneExt = 'Invalid'
          errors.locations[id] = locationErrors
        }

        if (altPhoneNumber?.trim()) {
          if (!isValidPhone(altPhoneNumber)) {
            locationErrors.altPhoneNumber = 'Must be valid phone number'
            errors.locations[id] = locationErrors
          }
        }

        if (phoneNumber.trim() !== '' && altPhoneNumber.trim() !== '') {
          if (phoneNumber?.trim() === altPhoneNumber?.trim()) {
            locationErrors.altPhoneNumber =
              'Must be different from primary phone'
            errors.locations[id] = locationErrors
          }
        }

        if (
          altPhoneExt?.trim() !== '' &&
          !isValidPhoneExtension(altPhoneExt ?? '')
        ) {
          locationErrors.altPhoneExt = 'Invalid'
          errors.locations[id] = locationErrors
        }

        if (firstName && firstName?.trim() !== '' && firstName?.length > 15) {
          locationErrors.firstName =
            'First name must be between 1 and 15 characters'
          errors.locations[id] = locationErrors
        }

        if (lastName && lastName?.trim() !== '' && lastName?.length > 20) {
          locationErrors.lastName =
            'Last name must be between 1 and 20 characters'
          errors.locations[id] = locationErrors
        }

        if (company && company?.trim() !== '' && company?.length > 35) {
          locationErrors.company = 'Company must be between 1 and 35 characters'
          errors.locations[id] = locationErrors
        }

        if (state && state?.trim().length > 2) {
          locationErrors.state = 'State is invalid'
          errors.locations[id] = locationErrors
        }

        if (postalCode && postalCode?.trim() !== '') {
          locationErrors.postalCode = validatePostalCode(postalCode)

          if (locationErrors.postalCode) {
            errors.locations[id] = locationErrors
          }
        }

        if (emailRequired || hasEmail) {
          const emailErrors = validateEmailCollection(
            emailAddresses,
            emailRequired,
            true,
          )

          if (emailErrors) {
            locationErrors.emailAddresses = emailErrors
            errors.locations[id] = locationErrors
          }
        }
      }

      if (Object.entries(errors.locations).length === 0) {
        errors = {}
      }

      return errors
    },
    [validatePostalCode],
  )

  const setInitialTouched = useCallback(
    (
      initialErrors: FormikErrors<CreateLocationFromAgreementProps>,
    ): FormikTouched<CreateLocationFromAgreementProps> => {
      const touched: FormikTouched<CreateLocationFromAgreementProps> = {}

      const locationErrors = initialErrors.locations

      touched.locations = Object.keys(locationErrors ?? {}).reduce(
        (acc, key) => {
          acc[key] = locationErrors
            ? Object.entries(locationErrors[key] ?? {}).reduce(
                (obj, [key, _]) => {
                  obj[key] = true
                  return obj
                },
                {},
              )
            : {}

          return acc
        },
        {},
      )

      return touched
    },
    [],
  )

  const onSubmit = useCallback(
    async (values, { setSubmitting, setFieldValue }) => {
      const { locations, entityId, entityType } = values

      let errorOccurred = false

      const errors = validate(values)

      setSubmitting(true)

      const validLocations: string[] = Object.keys(locations).filter(
        (key) => !Object.hasOwn(errors.locations ?? {}, key),
      )

      for (const locId of validLocations) {
        if (!locations[locId].isInvalid) continue

        const locationInput = formModelToLocationInput(
          locations[locId],
          entityId,
          entityType,
        )

        const result = await dispatch(addEditEntityLocation(locationInput))

        if (!addEditEntityLocation.fulfilled.match(result)) {
          errorOccurred = true
          break
        } else {
          setFieldValue(`locations.${locId}.isInvalid`, false)
        }
      }

      setSubmitting(false)

      if (errorOccurred) {
        toast.error('Unexpected error occurred.')
      }
    },
    [dispatch],
  )

  return { validate, setInitialTouched, onSubmit }
}

export function locationToFormModel(
  location: ValidatedLocation,
): CreateServiceLocationFormModel {
  const {
    id,
    locationTypeId,
    locationType,
    divisionId,
    division,
    firstName,
    lastName,
    company,
    street1,
    street2,
    city,
    stateAbbreviation,
    postalCode,
    county,
    country,
    latitude,
    longitude,
    phoneNumber,
    phoneExt,
    altPhoneNumber,
    altPhoneExt,
    billingLocation,
    branchId,
    branch,
    contact,
    isInvalid,
  } = location

  return {
    id,
    branchId: !branchId || branchId === 0 ? '' : `${branchId}`,
    branch: branch ?? '',
    locationTypeId:
      !locationTypeId || locationTypeId === '' ? '' : locationTypeId,
    locationType: locationType ?? '',
    divisionId: !divisionId || divisionId === '0' ? '' : divisionId,
    division: division ?? '',
    firstName: firstName ?? '',
    lastName: lastName ?? '',
    company: company ?? '',
    address1: street1 ?? '',
    address2: street2 ?? '',
    city: city ?? '',
    state: stateAbbreviation ?? '',
    postalCode: postalCode ?? '',
    county: county ?? '',
    country: country ?? '',
    lat: latitude ?? 0,
    lng: longitude ?? 0,
    phoneNumber: phoneNumber ?? '',
    phoneExt: phoneExt ?? '',
    altPhoneNumber: altPhoneNumber ?? '',
    altPhoneExt: altPhoneExt ?? '',
    updateBillTo: billingLocation ? false : true,
    billingLocation,
    contactMethod: contact?.contactMethod ?? '',
    contactTime: contact?.contactTime ? contact.contactTime.split(',') : [],
    emailAddresses: contact?.emailAddresses ?? [],
    isInvalid: isInvalid,
  }
}

export function formModelToLocationInput(
  model: CreateServiceLocationFormModel,
  entityId: number,
  entityType: ENTITY_TYPES,
  externalLocationId?: string | null,
): LocationInput {
  return {
    ...model,
    externalLocationId: externalLocationId,
    entityId,
    entityType,
    updateOnlyParentProduct: entityId === -1 || model.id == -1,
    updateBillTo: false,

    contact: {
      ...model,
      id: model.contactId,
      street1: model.address1,
      street2: model.address2,
      stateAbbreviation: model.state,
      county: model.county,
      latitude: model.lat,
      longitude: model.lng,
      emailAddresses: model.emailAddresses,
      contactTime: model.contactTime ? model.contactTime.join(',') : undefined,
    },
  }
}
